/**
 * 3D Geometry helpers — V14.63 Sprint 26F.
 *
 * Pure functions used by all 3D viewers (Shoebox, BuildingStack, Exterior).
 *
 * Coordinate system convention :
 *   • Input geometry uses canvas-pixel coordinates (px), with Y axis going
 *     DOWN (Konva / screen coords).
 *   • Output 3D coordinates use meters with the X axis going right,
 *     Y axis going UP (Three.js default), Z axis going forward (toward camera).
 *   • We map (px, py) → (px * scale, -py * scale) so the canvas Y-down becomes
 *     world Y-up (3D land surface is the XZ plane in three.js with Y as
 *     altitude → we'll lay floors flat on XZ and use Y for vertical extrusion).
 *
 * To map a 2D point on a floor plan into the 3D world :
 *    worldX = (px - cx) * scale_m_per_px
 *    worldZ = (py - cy) * scale_m_per_px         (Y-down → Z-forward)
 *    worldY = floor.bottom_z + (z_offset_in_floor)
 *
 * `cx, cy` are the centroid of the footprint so the building is centered
 * around the origin in XZ. This makes orbit-camera framing intuitive.
 */

import * as THREE from 'three';

// Default heights (meters) per level when the floor doc has no `height_m`.
export const DEFAULT_FLOOR_HEIGHTS_M = {
  '-2': 2.20,
  '-1': 2.20,   // Sous-sol
  '0':  2.50,   // RDC
  '1':  2.50,   // R+1
  '2':  2.30,   // R+2
  '3':  2.20,   // Combles
};

// Color palettes shared between viewers, keyed on wall.kind.
export const WALL_COLORS = {
  porteur:       '#06b6d4',  // cyan
  cloison_fine:  '#ef4444',  // red
  cloison_basse: '#a855f7',  // purple
  mur_invisible: '#22c55e',  // green — limite virtuelle (V14.96)
  default:       '#94a3b8',  // slate
};

/** V14.96 — Mur invisible : pointillé posé au sol (aucun volume). */
export const buildVirtualWallLine = (THREE, { x1, z1, x2, z2, y = 0.01 }) => {
  const geo = new THREE.BufferGeometry().setFromPoints([
    new THREE.Vector3(x1, y, z1), new THREE.Vector3(x2, y, z2),
  ]);
  const line = new THREE.Line(geo, new THREE.LineDashedMaterial({
    color: 0x22c55e, dashSize: 0.25, gapSize: 0.15, transparent: true, opacity: 0.9,
  }));
  line.computeLineDistances();
  return line;
};

export const STAIRS_COLOR = '#f59e0b';     // amber
export const WINDOW_COLOR = '#bae6fd';     // sky-200 (glassy)
export const DOOR_COLOR   = '#fde68a';     // amber-200 (wood)
export const TERRAIN_COLOR = '#365314';    // dark green
export const FLOOR_SLAB_COLOR = '#27272a'; // zinc-800

/**
 * Compute a stable centroid (px) from a list of wall endpoints.
 * Falls back to the centroid of the terrain polygon, then (0,0).
 */
export const computeCentroidPx = (walls = [], terrain = []) => {
  const pts = [];
  for (const w of walls) {
    if (Number.isFinite(w?.x1) && Number.isFinite(w?.y1)) pts.push([w.x1, w.y1]);
    if (Number.isFinite(w?.x2) && Number.isFinite(w?.y2)) pts.push([w.x2, w.y2]);
  }
  if (pts.length === 0) {
    for (const v of terrain) {
      const x = Array.isArray(v) ? v[0] : v?.x;
      const y = Array.isArray(v) ? v[1] : v?.y;
      if (Number.isFinite(x) && Number.isFinite(y)) pts.push([x, y]);
    }
  }
  if (pts.length === 0) return { cx: 0, cy: 0 };
  const sx = pts.reduce((acc, p) => acc + p[0], 0);
  const sy = pts.reduce((acc, p) => acc + p[1], 0);
  return { cx: sx / pts.length, cy: sy / pts.length };
};

/**
 * Convert one wall (in canvas-px) into a 3D extruded BoxGeometry params:
 *   { center: [x, y, z], size: [w, h, d], rotationY }
 * x, z are meters in world space (Y-up).  height (h) is in meters.
 */
export const wallToBoxParams = ({ wall, scaleMPerPx, height, bottomZ, cx, cy }) => {
  const mx1 = (wall.x1 - cx) * scaleMPerPx;
  const mz1 = (wall.y1 - cy) * scaleMPerPx;   // py → -pz so wall direction matches view
  const mx2 = (wall.x2 - cx) * scaleMPerPx;
  const mz2 = (wall.y2 - cy) * scaleMPerPx;
  const dx = mx2 - mx1;
  const dz = mz2 - mz1;
  const length = Math.hypot(dx, dz);
  const thicknessM = Math.max(0.05, (wall.thickness || 8) * scaleMPerPx);
  const angle = -Math.atan2(dz, dx);   // negate because Y axis up: rotateY
  return {
    center: [(mx1 + mx2) / 2, bottomZ + height / 2, (mz1 + mz2) / 2],
    size:   [length, height, thicknessM],
    rotationY: angle,
    kind: wall.kind || 'default',
  };
};

/**
 * Build a THREE.Shape from a 2D polygon (px) using a centroid + scale.
 * Returns null if fewer than 3 valid vertices.
 */
export const polygonToShape = ({ polygon, scaleMPerPx, cx, cy }) => {
  const pts = (polygon || []).map((v) => {
    const x = Array.isArray(v) ? v[0] : v?.x;
    const y = Array.isArray(v) ? v[1] : v?.y;
    if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
    return new THREE.Vector2((x - cx) * scaleMPerPx, (y - cy) * scaleMPerPx);
  }).filter(Boolean);
  if (pts.length < 3) return null;
  return new THREE.Shape(pts);
};

/**
 * Bounding box of a list of px points (walls or terrain).
 */
export const boundsPx = (walls = [], terrain = []) => {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  const push = (x, y) => {
    if (!Number.isFinite(x) || !Number.isFinite(y)) return;
    minX = Math.min(minX, x); maxX = Math.max(maxX, x);
    minY = Math.min(minY, y); maxY = Math.max(maxY, y);
  };
  for (const w of walls) { push(w.x1, w.y1); push(w.x2, w.y2); }
  for (const v of terrain) {
    push(Array.isArray(v) ? v[0] : v?.x, Array.isArray(v) ? v[1] : v?.y);
  }
  if (!Number.isFinite(minX)) return { minX: 0, minY: 0, maxX: 0, maxY: 0, w: 0, h: 0 };
  return { minX, minY, maxX, maxY, w: maxX - minX, h: maxY - minY };
};

/**
 * Normalize / fall back the floor height in meters.
 *
 * Sprint 30 : the Édition-Plan flow stores the ceiling height under
 * `hsp_m` (on `db.floors`), while the Single-House standalone flow uses
 * `height_m`. We accept BOTH transparently — the first finite value
 * wins ; fallback to the typology default per `level`.
 */
export const resolveFloorHeight = (floor) => {
  const h1 = floor?.hsp_m;
  const h2 = floor?.height_m;
  if (Number.isFinite(h1) && h1 > 0) return h1;
  if (Number.isFinite(h2) && h2 > 0) return h2;
  const lvl = String(floor?.level ?? 0);
  return DEFAULT_FLOOR_HEIGHTS_M[lvl] || 2.5;
};

/**
 * Compute the bottom_z / top_z of each floor (meters above ground).
 *   - level 0 sits at z=0
 *   - level >0 stacks upward (RDC.top = R+1.bottom, ...)
 *   - level <0 stacks downward (RDC.bottom = SS.top, ...)
 *
 * Returns map keyed by floor_id.
 */
export const buildStackLayout = (floors = []) => {
  const sorted = [...floors].sort((a, b) => (a.level ?? 0) - (b.level ?? 0));
  const out = {};
  // Above ground.
  let z = 0;
  for (const f of sorted.filter((x) => (x.level ?? 0) >= 0)) {
    const h = resolveFloorHeight(f);
    out[f.floor_id] = { bottom_z: +z.toFixed(3), top_z: +(z + h).toFixed(3), height: h };
    z += h;
  }
  // Below ground.
  z = 0;
  for (const f of sorted.filter((x) => (x.level ?? 0) < 0).sort((a, b) => (b.level ?? 0) - (a.level ?? 0))) {
    const h = resolveFloorHeight(f);
    out[f.floor_id] = { bottom_z: +(z - h).toFixed(3), top_z: +z.toFixed(3), height: h };
    z -= h;
  }
  return out;
};

/**
 * Resolve a floor's alignment transform with safe defaults.
 * `transform = { tx, ty, scale, rot }` — units : canvas px / radians.
 */
export const resolveTransform = (floor) => {
  const t = floor?.geometry?.transform || floor?.transform || {};
  return {
    tx:    Number.isFinite(t.tx)    ? +t.tx    : 0,
    ty:    Number.isFinite(t.ty)    ? +t.ty    : 0,
    scale: Number.isFinite(t.scale) && t.scale > 0 ? +t.scale : 1,
    rot:   Number.isFinite(t.rot)   ? +t.rot   : 0,
  };
};

/**
 * Apply a 2D affine transform (around (0,0)) to a px point.
 */
export const applyTransform = (px, py, transform) => {
  const s = transform.scale;
  const c = Math.cos(transform.rot);
  const sn = Math.sin(transform.rot);
  const x = px * s;
  const y = py * s;
  return {
    x: c * x - sn * y + transform.tx,
    y: sn * x + c * y + transform.ty,
  };
};


// V14.91 — Cloison basse : mur coupé à 1,10 m + garde-corps (pièce ouverte / extérieure).
// V14.92 — hauteur réglable (0,90 / 1,10 / 1,30) + style de garde-corps (vitré / barreaudé / plein).
export const LOW_WALL_HEIGHT_M = 1.10;
export const GUARDRAIL_HEIGHT_M = 0.90;
export const LOW_WALL_HEIGHTS = [0.90, 1.10, 1.30];
export const RAILING_STYLES = [
  { value: 'vitre', label: 'Vitré' },
  { value: 'barreaude', label: 'Barreaudé' },
  { value: 'plein', label: 'Plein' },
  { value: 'comptoir', label: 'Comptoir / bar' },   // V15.07 — bloc plein + plateau, profondeur bar_depth_m
];
export const BAR_DEPTH_M = 0.6;

/** Hauteur effective d'un mur : override (soupente) > cloison basse (low_height_m, déf. 1,10 m) > HSP. */
export const effectiveWallHeight = (w, floorHeight) => {
  if (Number.isFinite(+w.height_override_m) && +w.height_override_m > 0) return Math.min(+w.height_override_m, floorHeight);
  if ((w.kind || w.type) === 'cloison_basse') {
    const h = Number.isFinite(+w.low_height_m) && +w.low_height_m > 0 ? +w.low_height_m : LOW_WALL_HEIGHT_M;
    return Math.min(h, floorHeight);
  }
  return floorHeight;
};

/**
 * Garde-corps posé sur l'arase d'un mur bas. `style` ∈ vitre | barreaude | plein.
 * `length` en mètres, groupe centré à l'origine (à positionner/tourner comme le mur).
 */
export const buildGuardrail = (THREE, { length, baseY, color = 0x334155, style = 'vitre', thickness = 0.12 }) => {
  const g = new THREE.Group();
  const mat = new THREE.MeshStandardMaterial({ color, roughness: 0.45, metalness: 0.6 });
  if (style === 'plein') {
    const parapet = new THREE.Mesh(
      new THREE.BoxGeometry(length, GUARDRAIL_HEIGHT_M, Math.max(0.08, thickness)),
      new THREE.MeshStandardMaterial({ color: 0xcbd5e1, roughness: 0.85, metalness: 0.02 }),
    );
    parapet.position.set(0, baseY + GUARDRAIL_HEIGHT_M / 2, 0);
    g.add(parapet);
    const cap = new THREE.Mesh(new THREE.BoxGeometry(length + 0.02, 0.03, Math.max(0.08, thickness) + 0.04), mat);
    cap.position.set(0, baseY + GUARDRAIL_HEIGHT_M + 0.015, 0);
    g.add(cap);
    return g;
  }
  const railR = 0.02;
  const top = new THREE.Mesh(new THREE.CylinderGeometry(railR, railR, length, 10), mat);
  top.rotation.z = Math.PI / 2;
  top.position.set(0, baseY + GUARDRAIL_HEIGHT_M, 0);
  g.add(top);
  const n = Math.max(2, Math.round(length / 1.2) + 1);
  for (let i = 0; i < n; i += 1) {
    const x = -length / 2 + (length * i) / (n - 1);
    const post = new THREE.Mesh(new THREE.BoxGeometry(0.04, GUARDRAIL_HEIGHT_M, 0.04), mat);
    post.position.set(x, baseY + GUARDRAIL_HEIGHT_M / 2, 0);
    g.add(post);
  }
  if (style === 'barreaude') {
    const bars = Math.max(2, Math.round(length / 0.11));
    const barGeo = new THREE.CylinderGeometry(0.006, 0.006, GUARDRAIL_HEIGHT_M - 0.04, 6);
    for (let i = 1; i < bars; i += 1) {
      const x = -length / 2 + (length * i) / bars;
      const bar = new THREE.Mesh(barGeo, mat);
      bar.position.set(x, baseY + GUARDRAIL_HEIGHT_M / 2, 0);
      g.add(bar);
    }
    return g;
  }
  const mid = new THREE.Mesh(new THREE.CylinderGeometry(railR * 0.6, railR * 0.6, length, 8), mat);
  mid.rotation.z = Math.PI / 2;
  mid.position.set(0, baseY + GUARDRAIL_HEIGHT_M * 0.5, 0);
  g.add(mid);
  const glass = new THREE.Mesh(
    new THREE.PlaneGeometry(length, GUARDRAIL_HEIGHT_M - 0.06),
    new THREE.MeshStandardMaterial({ color: 0x93c5fd, transparent: true, opacity: 0.18, roughness: 0.1, metalness: 0.2, side: THREE.DoubleSide, depthWrite: false }),
  );
  glass.position.set(0, baseY + GUARDRAIL_HEIGHT_M / 2, 0);
  g.add(glass);
  return g;
};
